/**
 * Helpers for reading Trusstor floor-plan SVG marker ids.
 *
 * Trusstor stamps per-area markers into the floor SVG with two id shapes,
 * varying by project / authoring tool:
 *   - bare:           `area2001`,        `workers2001`
 *   - floor-prefixed: `f17area17C`,      `f17workers17C`
 * Adobe Illustrator additionally suffixes duplicate ids with `_<n>`
 * (`workers17C_2`). The area id is always the run that follows the marker
 * token — never a fixed-width prefix strip. This mirrors
 * trusstor_dashboard's `getFloorSvgPoints` (`split(id, 'workers').pop()`)
 * and `getAreaIdFromElementId` (which special-cases the `f<floor>` prefix).
 */

/**
 * Extracts the area id from a marker element id, given the marker token
 * (`'workers'` or `'area'`). Returns `null` when the id carries no trailing
 * area id (e.g. the capitalised `Workers` group container, or a token-only
 * id) so callers skip it rather than matching an empty area.
 */
export function markerAreaId(elementId: string, token: 'workers' | 'area'): string | null {
  // Strip the Illustrator dedupe suffix first (mirrors trusstor's `cleanId`).
  const clean = elementId.split('_')[0] ?? '';
  const parts = clean.split(token);
  if (parts.length < 2) return null;
  const areaId = parts[parts.length - 1];
  return areaId || null;
}

/**
 * Builds a CSS selector that matches the area-outline element(s) for a given
 * (data-side) area id, covering both bare (`area17C`) and floor-prefixed
 * (`f17area17C`) shapes, with or without the Illustrator `_<n>` suffix.
 * The id ends with `area<areaId>` (ends-with covers the optional `f<floor>`
 * prefix); the contains-with-`_` variant covers the dedupe suffix. The `i`
 * flag matches case-insensitively: the data-side area id reaches us
 * lower-cased, while the SVG stamps mixed case (`f17area17C`).
 */
export function areaHighlightSelector(areaId: string): string {
  // The value lands inside a CSS string AND, for the floor-area highlight, in
  // injected `<style>` rule text. Escape backslash + quote (string delimiters)
  // plus `}` / `]`, which would otherwise let a malformed id break out of the
  // attribute selector or the rule block (defence-in-depth — ids come from the
  // floor SVG, not user input).
  const escaped = areaId.replace(/[\\"}\]]/g, '\\$&');
  return `[id$="area${escaped}" i], [id*="area${escaped}_" i]`;
}

/** A pixel-space, axis-aligned rectangle (screen/container coordinates). */
export interface IScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The on-screen rect of a root SVG's *rendered plan content* — the viewBox
 * region as actually painted, including any `preserveAspectRatio` letterboxing.
 *
 * The `<svg>` element's own border box is NOT the plan. When that box has a
 * different aspect ratio than the viewBox (e.g. the Trusstor plans carry
 * `height="100%"` with the width clamped by CSS `max-width:100%`, so a square
 * 1200×1200 viewBox lands in a tall element box), `preserveAspectRatio` (default
 * `xMidYMid meet`) fits the plan inside with empty bands top/bottom. Pins and
 * area labels are positioned as a percentage of the plan, so measuring them
 * against the element box drifts them into those bands. Projecting the viewBox
 * corners through the root `getScreenCTM()` yields the true painted rect at any
 * container shape. Falls back to the element's client rect when the SVG carries
 * no usable viewBox, and returns `null` when it isn't laid out yet (no CTM /
 * zero-size) so callers can skip rather than guard each call.
 */
export function renderedPlanRect(svg: SVGSVGElement): IScreenRect | null {
  // Optional-chain the SVG geometry APIs: jsdom (test env) implements none of
  // them, so we fall back to the element's client rect there and in any browser
  // where the SVG carries no usable viewBox / isn't yet laid out.
  const ctm = svg.getScreenCTM?.();
  const vb = svg.viewBox?.baseVal;
  if (!ctm || !vb || vb.width === 0 || vb.height === 0 || typeof svg.createSVGPoint !== 'function') {
    const r = svg.getBoundingClientRect();
    return r.width === 0 || r.height === 0
      ? null
      : { left: r.left, top: r.top, width: r.width, height: r.height };
  }
  const project = (x: number, y: number) => {
    const point = svg.createSVGPoint();
    point.x = x;
    point.y = y;
    return point.matrixTransform(ctm);
  };
  const topLeft = project(vb.x, vb.y);
  const bottomRight = project(vb.x + vb.width, vb.y + vb.height);
  return {
    left: Math.min(topLeft.x, bottomRight.x),
    top: Math.min(topLeft.y, bottomRight.y),
    width: Math.abs(bottomRight.x - topLeft.x),
    height: Math.abs(bottomRight.y - topLeft.y),
  };
}

/**
 * Projects an SVG element's `getBBox()` (user-space) into a pixel rect in the
 * page's screen coordinate system via its own `getScreenCTM()`. Returns `null`
 * when the SVG isn't laid out yet (no CTM / detached / `display:none`), so
 * callers can skip rather than guard each call. Width/height are the absolute
 * delta of two projected opposite corners — correct for the axis-aligned
 * transforms Trusstor floor plans use (they aren't rotated).
 */
export function svgElementScreenRect(el: SVGGraphicsElement): IScreenRect | null {
  const svg = el.ownerSVGElement;
  if (!svg) return null;
  try {
    const ctm = el.getScreenCTM();
    if (!ctm) return null;
    const bbox = el.getBBox();
    const topLeft = svg.createSVGPoint();
    topLeft.x = bbox.x;
    topLeft.y = bbox.y;
    const bottomRight = svg.createSVGPoint();
    bottomRight.x = bbox.x + bbox.width;
    bottomRight.y = bbox.y + bbox.height;
    const screenTopLeft = topLeft.matrixTransform(ctm);
    const screenBottomRight = bottomRight.matrixTransform(ctm);
    return {
      left: screenTopLeft.x,
      top: screenTopLeft.y,
      width: Math.abs(screenBottomRight.x - screenTopLeft.x),
      height: Math.abs(screenBottomRight.y - screenTopLeft.y),
    };
  } catch {
    // getBBox / getScreenCTM throw before the SVG is laid out.
    return null;
  }
}

/** The centre of a marker (`area<id>` / `workers<id>`) in the container's pixel
 *  coordinate system, tagged with its area id. */
export interface ISvgAnchor {
  areaId: string;
  /** Centre x in container px — chips position via translate(-50%,-50%). */
  x: number;
  y: number;
}

/**
 * Reads the centre of every SVG element whose id contains `token` (`workers` or
 * `area`), in the container's pixel coordinate system. Trusstor floor SVGs stamp
 * `workers<id>` anchors inside a `display:none` group, so `getBoundingClientRect`
 * returns 0×0 — the centre is computed via `getBBox()` projected through
 * `getScreenCTM()` (see `svgElementScreenRect`), exactly like trusstor_dashboard's
 * `getFloorSvgPoints`. The area id is recovered as the run after the token
 * (`markerAreaId`), tolerating floor-prefixed / Illustrator-deduped ids.
 *
 * Shared by `MonitorPage/MainView/FloorDrawer` (all anchors) and
 * `EquipmentPage/SiteMap/EquipmentFloorPlan` (the one matching the selected area).
 */
export function readSvgAnchorCentres(container: HTMLElement, token: 'workers' | 'area'): ISvgAnchor[] {
  const containerRect = container.getBoundingClientRect();
  const out: ISvgAnchor[] = [];
  container.querySelectorAll<SVGGraphicsElement>(`[id*="${token}"]`).forEach((el) => {
    const areaId = markerAreaId(el.id, token);
    if (!areaId) return;
    const rect = svgElementScreenRect(el);
    if (!rect) return;
    const x = rect.left + rect.width / 2 - containerRect.left;
    const y = rect.top + rect.height / 2 - containerRect.top;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    // Anchors at literally (0,0) with no size are placeholders for empty groups
    // that would render the chip in the corner; skip them.
    if (x === 0 && y === 0 && rect.width === 0 && rect.height === 0) return;
    out.push({ areaId, x, y });
  });
  return out;
}

/**
 * Tightens a floor SVG's `viewBox` to its content bounding box (plus a small
 * margin) so artboard whitespace doesn't push the plan off-centre, and drops the
 * fixed width/height so it scales to its container. No-op (silently) when the SVG
 * isn't laid out yet — `getBBox()` throws while detached / `display:none`.
 * Shared by every floor-plan surface (FloorDrawer, EquipmentFloorPlan).
 */
export function tightenSvgViewBox(svg: SVGSVGElement): void {
  try {
    const bbox = svg.getBBox();
    if (bbox.width > 0 && bbox.height > 0) {
      const m = Math.max(bbox.width, bbox.height) * 0.015;
      svg.setAttribute('viewBox', `${bbox.x - m} ${bbox.y - m} ${bbox.width + m * 2} ${bbox.height + m * 2}`);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
    }
  } catch {
    // Detached / display:none — leave the viewBox alone.
  }
}
